import { useEffect } from 'react';

// Matches the shortcuts documented at ucat.edu.au/prepare/test-tools/:
// Alt+N next, Alt+P previous, Alt+F flag, Alt+C calculator, Alt+V navigator,
// and a bare letter key (e.g. "B") selects that lettered answer option
// directly. Drag-and-drop questions are excluded on purpose — the official
// site states those must be answered with the mouse.
export function useKeyboardShortcuts({
  onNext,
  onPrevious,
  onFlag,
  onToggleCalculator,
  onToggleNavigator,
  onSelectOption,
  optionLetters = [],
  enabled = true,
}) {
  useEffect(() => {
    if (!enabled) return;

    const handleKeyDown = (e) => {
      const target = e.target;
      const isTyping = target && ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName);
      if (isTyping) return;

      if (e.altKey) {
        switch (e.key.toLowerCase()) {
          case 'n':
            e.preventDefault();
            onNext?.();
            return;
          case 'p':
            e.preventDefault();
            onPrevious?.();
            return;
          case 'f':
            e.preventDefault();
            onFlag?.();
            return;
          case 'c':
            e.preventDefault();
            onToggleCalculator?.();
            return;
          case 'v':
            e.preventDefault();
            onToggleNavigator?.();
            return;
          default:
            return;
        }
      }

      const letter = e.key.toUpperCase();
      if (optionLetters.includes(letter)) {
        e.preventDefault();
        onSelectOption?.(letter);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [enabled, onNext, onPrevious, onFlag, onToggleCalculator, onToggleNavigator, onSelectOption, optionLetters]);
}

// Extracts the leading letter from an option string like "B) 18" -> "B".
export function optionLetter(optionText) {
  const match = /^([A-Z])\)/.exec(optionText || '');
  return match ? match[1] : null;
}

export const SHORTCUT_HINTS = [
  { key: 'A–E', action: 'Select that lettered answer option' },
  { key: 'Alt + N', action: 'Next question' },
  { key: 'Alt + P', action: 'Previous question' },
  { key: 'Alt + F', action: 'Flag for review' },
  { key: 'Alt + C', action: 'Calculator' },
  { key: 'Alt + V', action: 'Navigator window' },
];
